import {
  compareProductUpdatesNewestFirst,
  type ProductUpdate,
} from './parseProductUpdates';

/** How many tags the combined card keeps from the whole unread set. */
export const WHATS_NEW_TAG_LIMIT = 4;

/**
 * First day an update can appear on the card. Earlier entries stay on
 * Product Updates only, including the history written when the card launched.
 */
export const WHATS_NEW_CARD_FROM = '2026-10-23';

export type WhatsNewMemory = {
  /** Recorded on the account. Selection does not use it. */
  firstOpenedOn: string;
  dismissedIds: ReadonlySet<string>;
};

export type WhatsNewNotice = {
  kind: 'single' | 'combined';
  /** Newest first. Combined notices include every unread update they cover. */
  updates: readonly ProductUpdate[];
};

/**
 * Pick the card to show, or nothing.
 *
 * An update stays until the user closes the card. Opening the changelog
 * does not hide it. Age and the first visit do not hide it. One open
 * update shows alone.
 * Two or more show as one card. A future date stays off the card until that day.
 * Anything published before {@link WHATS_NEW_CARD_FROM} stays off the card.
 */
export function selectWhatsNewNotice(
  updates: readonly ProductUpdate[],
  memory: WhatsNewMemory,
  today: string
): WhatsNewNotice | null {
  const unread = updates
    .filter((update) => update.showInPopup)
    .filter((update) => update.publishedOn >= WHATS_NEW_CARD_FROM)
    .filter((update) => !memory.dismissedIds.has(update.id))
    .filter((update) => update.publishedOn <= today)
    .sort(compareProductUpdatesNewestFirst);

  if (unread.length === 0) return null;
  if (unread.length === 1) return { kind: 'single', updates: unread };
  return { kind: 'combined', updates: unread };
}

export function whatsNewTags(notice: WhatsNewNotice): string[] {
  const tags: string[] = [];
  const seen = new Set<string>();
  for (const update of notice.updates) {
    for (const tag of update.tags) {
      if (seen.has(tag)) continue;
      seen.add(tag);
      tags.push(tag);
      if (tags.length === WHATS_NEW_TAG_LIMIT) return tags;
    }
  }
  return tags;
}

export const PRODUCT_UPDATES_DOC_PATH = '/product-updates';

export function whatsNewDocPath(notice: WhatsNewNotice): string {
  const update = notice.updates[0];
  if (notice.kind === 'single' && update) {
    return `${PRODUCT_UPDATES_DOC_PATH}#${update.id}`;
  }
  return PRODUCT_UPDATES_DOC_PATH;
}
